import { createStore } from 'react-sweet-state';
import { cloneDeep } from 'lodash';

import { BRIEF_TYPE, mapExtractedContentForEditing } from 'src/data/brief';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { EDITOR_TYPE } from 'src/data/project';
import { extractBrief } from 'src/graphql/brief';
import { classifyLongForm } from 'src/graphql/longFormBuilder';
import { classifyTemplate } from 'src/graphql/project';
import * as ls from 'src/lib/localData';
import { EMPTY_PARAGRAPH_HTML } from 'src/lib/slate';
import { waitForBriefExtraction } from 'src/services/waitForBriefExtraction';
import { extractUrls } from 'src/utils/extractUrl';

import { containsBlog, prepareToolList } from '../../lib';

const INITIAL_STATE = {
  promptText: EMPTY_PARAGRAPH_HTML,
  promptData: null,
  classifyingTemplate: false,
};

const alignLongFormTemplate = (template) => {
  if (template === 'blog') {
    return GENERATION_TOOL.blogDescription;
  }

  return GENERATION_TOOL.longFormBlank;
};

export const PRIVATE_ACTIONS = {
  initStore:
    () =>
    ({ setState }) => {
      setState({
        ...cloneDeep(INITIAL_STATE),
        editorType: ls.selectedEditorType.read(EDITOR_TYPE.longForm),
      });
    },

  extractBriefs:
    (urlsToExtract) =>
    ({ dispatch }, { currentCustomerId }) => {
      if (!urlsToExtract || urlsToExtract.length === 0) {
        return Promise.resolve([]);
      }

      const promises = urlsToExtract.map((url) =>
        extractBrief({
          customerId: currentCustomerId,
          url,
          briefType: BRIEF_TYPE.unifiedContent, // for url import, brief type should always be unifiedContent
        })
          .then(({ id }) => waitForBriefExtraction(id))
          .then((extractedBrief) => {
            const { contentTaxonomy, ...newBrief } = mapExtractedContentForEditing(extractedBrief);
            const body = newBrief?.article || '';
            return {
              url,
              extractedBrief: body,
            };
          })
      );

      return Promise.all(promises);
    },

  prepareUrlExtractions:
    () =>
    async ({ getState, dispatch }) => {
      const { promptData } = getState();

      // Get URLs from prompt data
      const content = promptData?.text;
      const urls = extractUrls(content, MAX_RESOURCE_PER_TYPE)?.urls || [];

      // Wait for briefs extraction
      const result = await dispatch(PRIVATE_ACTIONS.extractBriefs(urls));
      return result || [];
    },
};

export const ACTIONS = {
  getPromptText:
    () =>
    ({ getState }) => {
      return getState().promptText;
    },

  getPromptData:
    () =>
    ({ getState }) => {
      return getState().promptData;
    },

  updatePrompt:
    (text, data) =>
    ({ setState }) => {
      setState({
        promptText: text,
        promptData: data,
      });
    },

  updateEditorType:
    (editorType) =>
    ({ setState }) => {
      ls.selectedEditorType.write(editorType);
      setState({ editorType });
    },

  classifyTemplate:
    () =>
    async ({ getState, setState, dispatch }, { currentCustomerId }) => {
      const { promptData, editorType } = getState();

      setState({ classifyingTemplate: true });
      try {
        const toolList = prepareToolList();
        const urlExtractions = await dispatch(PRIVATE_ACTIONS.prepareUrlExtractions());

        if (editorType === EDITOR_TYPE.shortForm) {
          return await classifyTemplate({
            customerId: currentCustomerId,
            toolList,
            allInstructionsData: promptData,
            urlExtractions,
          });
        }

        const blogFound = containsBlog(promptData.text);

        if (!blogFound) {
          return GENERATION_TOOL.longFormBlank;
        }

        const longFormTemplate = await classifyLongForm({
          customerId: currentCustomerId,
          allInstructionsData: promptData,
          urlExtractions,
        });

        return alignLongFormTemplate(longFormTemplate?.template);
      } finally {
        setState({ classifyingTemplate: false });
      }
    },
};

export const MainStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'new-project-prompt-widget-main',
});
